/**
 * "BUGÜN DİKKAT GEREKTİREN" KURAL SETİ
 * Kaynak: `docs/Dikkat_Gerektiren_Kural_Seti.docx` (tam metin) + devir dokumani Bolum 7.
 *
 * Kapsam ANLIKTIR ("şu an"), donem seciminden BAGIMSIZ. Yalniz acik/aktif isler
 * girer; tamamlanan isler bu alana dusmez.
 *
 * ⚠️ ESIKLER TASLAKTIR. Kural seti belgesinin "Kalibrasyon" bolumu bunlari
 *    acikca "sahadaki gercege gore birlikte netlestirelim" diye isaretliyor.
 *    Bu yuzden koda GOMULMEZ — `sistem_ayarlar` tablosundan gelir ve sahada
 *    Bedirhan'la oturup canli ayarlanabilir.
 */
import { KONTROL_PERIYOT_GUN } from './kontrol.js';
/** Tetikleyici kimlikleri — ayar anahtarlariyla birebir. */
export const TETIKLEYICILER = [
    'guvenlik_dongusu',
    'kapanis_bloklu',
    'kontrol_onay_bekliyor',
    'kesif_bekliyor',
    'malzeme_bekliyor',
    'kurulum_bekliyor',
    'sokum_revize_bekliyor',
    'izin_override',
    'haftalik_kontrol',
    'delegasyon_bekliyor',
    'gunluk_rapor',
];
/** Belgedeki taslak degerler. Sahada netlesecek. */
export const VARSAYILAN_ESIKLER = {
    kontrol_onay_bekliyor: [2, 24], // 2 saat → Uyarı, 1 gün → Kritik
    kesif_bekliyor: [48, 96], // 2 gün → 4 gün
    malzeme_bekliyor: [24, 72], // 1 gün → 3 gün
    kurulum_bekliyor: [12, null], // ½ gün, kritiğe terfi etmez
    sokum_revize_bekliyor: [24, null],
    delegasyon_bekliyor: [24, null],
    gunluk_rapor_saati: 16,
    izin_override_adet: 3,
    erteleme_saat: 4,
};
/** Kural seti Bölüm 7 — hiç kart yoksa gösterilecek metin. */
export const DIKKAT_BOS_METIN = 'Bugün dikkat gerektiren yok — akış temiz.';
const saatFarki = (a, b) => (b.getTime() - a.getTime()) / 3_600_000;
/**
 * Yas esigine gore sinif: esik asilinca bir ust kademeye cikar.
 *
 * ⚠️ ESIGIN ALTINDA KART YOKTUR. Kural seti "Kontrol-onay bekliyor · UYARI →
 * KRİTİK · 2 saat → 1 gün" diyor: 2 saatten once bu is dikkat gerektirmez.
 * Aksi halde her acik is panoda gorunur ve "bugun neye bakmaliyim" ekrani
 * ise yaramaz hale gelir — asil amac tam bunun tersi.
 */
function terfi(yasSaat, [uyari, kritik]) {
    if (kritik != null && yasSaat >= kritik)
        return 'kritik';
    if (yasSaat >= uyari)
        return 'uyari';
    return null;
}
/**
 * Bir is icin gecerli TUM tetikleyicileri uretir.
 * Sonra `tekKartaIndirge` en yuksek skorluyu secer — AYNI IS IKI KEZ GORUNMEZ.
 */
export function isIcinKartlar(j, simdi, esikler) {
    const k = [];
    const yas = saatFarki(j.durumZamani, simdi);
    const ortak = { jobId: j.id, metraj: j.metraj };
    const ad = j.isEmriNo ?? j.unite;
    // Tamamlanan, ertelenen ve IPTAL edilen isler bu alana DUSMEZ.
    if (['kapali', 'ertelendi', 'iptal'].includes(j.durum))
        return k;
    /* A) GÜVENLİK & SERT GEÇİT — anında kritik */
    // Güvenlik döngüsü: Kritikler arasında da MUTLAK EN ÜSTTE.
    if (!j.yesilKart) {
        k.push({
            ...ortak,
            tetikleyici: 'guvenlik_dongusu',
            sinif: 'kritik',
            baslik: 'Güvenlik döngüsü — yeşil kart kaldırıldı',
            detay: `${ad} · ${j.tanim}`,
            yasSaat: yas,
            mutlakUst: true,
        });
    }
    // Kapanış bloklu: onaylı ama iş emri kâğıt fotosu eksik (K3).
    // Göç edilen kayıt bu karttan MUAF — bkz. `gocEdilmis` yorumu.
    if (!j.gocEdilmis && !j.isEmriFotoVar && (j.durum === 'ayakta' || j.durum === 'kontrol')) {
        k.push({
            ...ortak,
            tetikleyici: 'kapanis_bloklu',
            sinif: 'kritik',
            baslik: 'Kapanamıyor — iş emri fotoğrafı eksik',
            detay: `${ad} · iş emrisiz açıldı, kapanış için kâğıt fotosu gerekli`,
            yasSaat: saatFarki(j.olusturmaZamani, simdi),
        });
    }
    /* B) DARBOĞAZ & BEKLEME — yaş esas, eşik aşılınca terfi */
    if (j.durum === 'kontrol') {
        const s = terfi(yas, esikler.kontrol_onay_bekliyor);
        if (s) {
            k.push({
                ...ortak,
                tetikleyici: 'kontrol_onay_bekliyor',
                sinif: s,
                baslik: 'Kontrol & onay bekliyor',
                detay: `${ad} · "ben kurdum" tamam — onay + 3 fotoğraf gerekli`,
                yasSaat: yas,
            });
        }
    }
    if (j.durum === 'giris') {
        const s = terfi(yas, esikler.kesif_bekliyor);
        if (s) {
            k.push({
                ...ortak,
                tetikleyici: 'kesif_bekliyor',
                sinif: s,
                baslik: `Keşif ${Math.floor(yas / 24)} gündür bekliyor`,
                detay: `${j.unite} · keşifçi: ${j.kesifciAd ?? 'atanmadı'}`,
                yasSaat: yas,
            });
        }
    }
    if (j.durum === 'kesif' && !j.malzemeSevk) {
        const s = terfi(yas, esikler.malzeme_bekliyor);
        if (s) {
            k.push({
                ...ortak,
                tetikleyici: 'malzeme_bekliyor',
                sinif: s,
                baslik: 'Malzeme bekliyor',
                detay: `${ad} · keşif bitti, sevk yok`,
                yasSaat: yas,
            });
        }
    }
    if (j.durum === 'hazirlik') {
        const s = terfi(yas, esikler.kurulum_bekliyor);
        if (s) {
            k.push({
                ...ortak,
                tetikleyici: 'kurulum_bekliyor',
                sinif: s,
                baslik: 'Kurulum bekliyor',
                detay: `${ad} · izin ve malzeme hazır, başlanmadı`,
                yasSaat: yas,
            });
        }
    }
    /*
     * REVIZE BITTI → SEF ONAYI BEKLIYOR.
     *
     * Ayri ve ONCELIKLI bir kart: is `revize` durumunda kalir ama artik sahada
     * yapilacak bir sey yoktur, top sefdedir. Onceki halde bu durum "emir
     * verildi, baslanmadi" diye gorunuyordu — hem yanlis hem de sefi
     * yonlendirmiyordu.
     */
    if (j.durum === 'revize' && j.revizeFazDurumu === 'bitti') {
        const s = terfi(yas, esikler.kontrol_onay_bekliyor);
        if (s) {
            k.push({
                ...ortak,
                tetikleyici: 'kontrol_onay_bekliyor',
                sinif: s,
                baslik: 'Revize onayı bekliyor',
                detay: `${ad} · revize bitti, şef kontrol & onayı bekliyor`,
                yasSaat: yas,
            });
        }
    }
    else if (j.durum === 'sokum' || j.durum === 'revize') {
        const s = terfi(yas, esikler.sokum_revize_bekliyor);
        if (s) {
            k.push({
                ...ortak,
                tetikleyici: 'sokum_revize_bekliyor',
                sinif: s,
                baslik: `${j.durum === 'sokum' ? 'Söküm' : 'Revizyon'} bekliyor`,
                detay: `${ad} · emir verildi, başlanmadı`,
                yasSaat: yas,
            });
        }
    }
    /* C) OVERRIDE & RİSK */
    if (j.izin === 'override') {
        k.push({
            ...ortak,
            tetikleyici: 'izin_override',
            sinif: 'bilgi',
            baslik: 'İzin alınmadan başlatılan iş',
            detay: `${ad} · sorumluluk kaydı düşüldü`,
            yasSaat: yas,
        });
    }
    /* D) RUTİN & PLANLI */
    // Haftalık kontrol vadesi — 7 günde bir.
    if (j.durum === 'ayakta') {
        const gecen = j.sonKontrolAt
            ? saatFarki(j.sonKontrolAt, simdi)
            : j.teslimAt
                ? saatFarki(j.teslimAt, simdi)
                : 0;
        if (gecen >= KONTROL_PERIYOT_GUN * 24) {
            k.push({
                ...ortak,
                tetikleyici: 'haftalik_kontrol',
                sinif: 'bilgi',
                baslik: 'Haftalık kontrol vadesi geldi',
                detay: `${ad} · son kontrol ${Math.floor(gecen / 24)} gün önce`,
                yasSaat: gecen,
            });
        }
    }
    // Delegasyon bekleyen yeni iş (keşifçi atanmamış).
    if (j.durum === 'giris' && !j.kesifciAd) {
        const s = terfi(yas, esikler.delegasyon_bekliyor) ?? 'bilgi';
        k.push({
            ...ortak,
            tetikleyici: 'delegasyon_bekliyor',
            sinif: s,
            baslik: 'Delegasyon bekleyen iş',
            detay: `${ad} · keşifçi atanmadı`,
            yasSaat: yas,
        });
    }
    return k;
}
/**
 * HER İŞ KENDİ KARTI — farkli is emirleri ASLA tek kartta birlesmez.
 * Ayni is birden cok tetikleyiciye uyarsa EN YUKSEK SKORLU tetikleyicisiyle
 * TEK kart olur (ayni is iki kez gorunmez).
 */
const SINIF_SKOR = { kritik: 3, uyari: 2, bilgi: 1 };
export function tekKartaIndirge(kartlar) {
    const enIyi = new Map();
    for (const k of kartlar) {
        const anahtar = k.jobId ?? k.tetikleyici;
        const mevcut = enIyi.get(anahtar);
        if (!mevcut || skor(k) > skor(mevcut))
            enIyi.set(anahtar, k);
    }
    return [...enIyi.values()];
}
function skor(k) {
    return SINIF_SKOR[k.sinif] * 1000 + (k.mutlakUst ? 500 : 0) + Math.min(k.yasSaat, 499);
}
/**
 * SIRALAMA (kural seti Bölüm 3):
 *   1. Önce SINIF: tüm Kritikler > tüm Uyarılar > tüm Bilgiler
 *   2. Sınıf içinde EN UZUN BEKLEYEN üstte (yaş esas — öncelik alanı yok, FIFO)
 *   3. Yaş eşitse METRAJI BÜYÜK üstte — tek eşitlik bozucu
 *   4. İSTİSNA: güvenlik döngüsü Kritikler arasında da MUTLAK EN ÜSTTE
 */
export function dikkatSirala(kartlar) {
    return [...kartlar].sort((a, b) => {
        if (a.mutlakUst !== b.mutlakUst)
            return a.mutlakUst ? -1 : 1;
        const s = SINIF_SKOR[b.sinif] - SINIF_SKOR[a.sinif];
        if (s !== 0)
            return s;
        if (b.yasSaat !== a.yasSaat)
            return b.yasSaat - a.yasSaat;
        return b.metraj - a.metraj;
    });
}
export function ertelenebilirMi(sinif) {
    return sinif !== 'kritik';
}
/** Uçtan uca: işlerden sıralı kart listesi üret. */
export function dikkatKartlari(isler, simdi, esikler = VARSAYILAN_ESIKLER, raporGonderildi = true, ertelemeler = []) {
    const hepsi = isler.flatMap((j) => isIcinKartlar(j, simdi, esikler));
    // Günlük rapor gönderilmedi — işe bağlı DEĞİL, tek kart.
    if (!raporGonderildi && simdi.getHours() >= esikler.gunluk_rapor_saati) {
        hepsi.push({
            jobId: null,
            tetikleyici: 'gunluk_rapor',
            sinif: 'bilgi',
            baslik: 'Günlük rapor gönderilmedi',
            detay: 'Gün sonu yaklaşıyor — şef onayıyla gider',
            yasSaat: simdi.getHours() - esikler.gunluk_rapor_saati,
            metraj: 0,
        });
    }
    /*
     * C) OVERRIDE & RİSK — "BİLGİ → UYARI · Biriktikçe".
     * Terfi YAŞA değil ADEDE bakar: tek override istisnadır, birikmesi
     * alışkanlıktır. Bu yüzden karar tek işe bakarak verilemez, tüm şantiyeye
     * bakılır — ve bu ancak burada, kartlar toplandıktan sonra mümkün.
     */
    const overrideler = hepsi.filter((k) => k.tetikleyici === 'izin_override');
    if (overrideler.length >= esikler.izin_override_adet) {
        for (const k of overrideler) {
            k.sinif = 'uyari';
            k.detay += ` · şantiyede ${overrideler.length} açık override`;
        }
    }
    /*
     * D) HAFTALIK KONTROL — TEK KART, SAYIYLA (kullanici istegi 07.09.2026).
     *
     * ⚠️ BU, "HER IS KENDI KARTI" KURALININ BILINCLI ISTISNASIDIR (bkz.
     *    `tekKartaIndirge` basligi). Gerekce sahadan geldi: ayakta duran 86+
     *    iskelenin vadesi ayni anda doluyor ve pano 125 tane ayni basligi
     *    tasiyan kartla doluyordu; "bugun neye bakmaliyim" sorusunun cevabi
     *    o yiginin altinda kayboluyordu. Ekran goruntusunde 131 kartin
     *    126'si bu tetikleyiciydi.
     *
     * ⚠️ INDIRGEME `tekKartaIndirge`DEN SONRA. Once calissaydi, hem "kapanis
     *    bloklu" hem "haftalik kontrol" olan bir is toplu sayiya girer ve
     *    kendi kritik karti kaybolurdu. Sonra calisinca yalniz GERCEKTEN
     *    haftalik kontrolle gorunen isler sayilir.
     *
     * Tek is kaldiysa toplu karta cevrilmez: "1 iskele" yazan bir liste karti,
     * isin kendisini gostermekten daha az bilgi tasir.
     */
    const indirgenmis = tekKartaIndirge(hepsi);
    const haftalik = indirgenmis.filter((k) => k.tetikleyici === 'haftalik_kontrol');
    const kalan = haftalik.length > 1
        ? indirgenmis.filter((k) => k.tetikleyici !== 'haftalik_kontrol')
        : indirgenmis;
    if (haftalik.length > 1) {
        const enEski = Math.max(...haftalik.map((k) => k.yasSaat));
        kalan.push({
            // ⚠️ `jobId: null` — kart bir ise degil LISTEYE gider. Istemci bunu
            //    gorup "/kurulu?vade=gecti" acar; erteleme de tum grubu erteler.
            jobId: null,
            tetikleyici: 'haftalik_kontrol',
            sinif: 'bilgi',
            baslik: 'Haftalık kontrol vadesi geldi',
            detay: `${haftalik.length} iskele · en eskisi ${Math.floor(enEski / 24)} gün önce`,
            yasSaat: enEski,
            // Siralamada en buyuk metraji temsil eder — grup, en agir isi kadar onemli.
            metraj: Math.max(...haftalik.map((k) => k.metraj)),
            adet: haftalik.length,
        });
    }
    const kartlar = dikkatSirala(kalan);
    // Ertelenmiş kartları düşür — kritikler ASLA düşmez.
    if (!ertelemeler.length)
        return kartlar;
    return kartlar.filter((k) => {
        if (!ertelenebilirMi(k.sinif))
            return true;
        const e = ertelemeler.find((x) => x.tetikleyici === k.tetikleyici && (x.jobId ?? null) === (k.jobId ?? null));
        return !e || e.bitisAt <= simdi;
    });
}
//# sourceMappingURL=dikkat.js.map